import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createGuideline } from '../api/guidelines.api';
import { useCurrentWorkspaceId } from '@/entities/workspace';
import { notificationService } from '@/lib/services/notification.service';
import type { GuidelineResponseDto, GuidelineCreateDto } from '@/lib/api/guidelines-client';
import { useTranslation } from 'react-i18next';

export interface CreateGuidelineParams {
  name: string;
  description: string;
  category: GuidelineCreateDto['category'];
  folderId?: string;
}

export const useCreateGuideline = () => {
  const { t } = useTranslation('knowledge');
  const queryClient = useQueryClient();
  const workspaceId = useCurrentWorkspaceId();

  return useMutation<GuidelineResponseDto | null, Error, CreateGuidelineParams>({
    mutationFn: async ({ name, description, category, folderId }) => {
      if (!workspaceId) throw new Error(t('guidelines.workspaceIdIsRequired'));

      const createDto: GuidelineCreateDto = {
        name,
        description,
        category,
        examples: [],
        assetIds: [],
        type: 'workspace',
        workspaceId,
        folderId,
      };
      return createGuideline(createDto);
    },
    onSuccess: (data, variables) => {
      if (variables.folderId) {
        void queryClient.invalidateQueries({
          queryKey: ['guidelines', 'folder', variables.folderId],
        });
      }
      void queryClient.invalidateQueries({ queryKey: ['folders', workspaceId] });
      if (data) {
        notificationService.showSuccess(
          t('guidelines.useCreateGuideline.guidelineCreated', { name: data.name }),
          {
            anchorOrigin: { vertical: 'bottom', horizontal: 'center' },
          },
        );
      }
    },
    onError: (error) => {
      notificationService.showError(
        error.message || t('guidelines.useCreateGuideline.failedToCreateGuideline'),
      );
    },
  });
};
